import {
  ANSPRECHPARTNER_TEAM_MAIL_DOKUMENTARTEN,
  type KontaktAnsprechpartner,
  type KontaktAnsprechpartnerIn,
} from "@/lib/kontakte";

export interface ApForm {
  anrede: string;
  vorname: string;
  nachname: string;
  telefonnummer: string;
  mobiltelefon: string;
  funktion: string;
  mail: string;
  teamMail: string;
  teamMailDokumentarten: string[];
}

export type ApFormUpdater = (current: ApForm) => ApForm;

export const EMPTY_AP_FORM: ApForm = {
  anrede: "",
  vorname: "",
  nachname: "",
  telefonnummer: "",
  mobiltelefon: "",
  funktion: "",
  mail: "",
  teamMail: "",
  teamMailDokumentarten: [],
};

export function orderedTeamMailArten(selected: string[] | null | undefined): string[] {
  const chosen = new Set(selected || []);
  return ANSPRECHPARTNER_TEAM_MAIL_DOKUMENTARTEN.map((art) => art.key).filter((key) =>
    chosen.has(key),
  );
}

export function toggleTeamMailDokumentart(selected: string[], key: string): string[] {
  return orderedTeamMailArten(
    selected.includes(key) ? selected.filter((item) => item !== key) : [...selected, key],
  );
}

/** Wendet eine Änderung auf den aktuellen Formularstand an, ohne andere Felder zu verwerfen. */
export function applyApFormUpdate(current: ApForm, updater: ApFormUpdater): ApForm {
  return updater(current);
}

/** Übernimmt einen gerade gespeicherten Ansprechpartner in die geladene Liste. */
export function mergeSavedAnsprechpartner(
  rows: KontaktAnsprechpartner[],
  saved: KontaktAnsprechpartner,
): KontaktAnsprechpartner[] {
  const index = rows.findIndex((row) => row.id === saved.id);
  if (index < 0) return [...rows, saved];
  return rows.map((row) => (row.id === saved.id ? saved : row));
}

function visibleText(value: string | null | undefined): string {
  return (value || "").trim();
}

/** Telefon, Mail und die übrigen sichtbaren Felder, unabhängig von null/Leerstring. */
export function ansprechpartnerFieldsMatch(
  left: KontaktAnsprechpartner,
  right: KontaktAnsprechpartner,
): boolean {
  const documents = (row: KontaktAnsprechpartner) =>
    orderedTeamMailArten(row.team_mail_dokumentarten).join("\0");
  return (
    visibleText(left.anrede) === visibleText(right.anrede) &&
    visibleText(left.vorname) === visibleText(right.vorname) &&
    visibleText(left.nachname) === visibleText(right.nachname) &&
    visibleText(left.telefonnummer) === visibleText(right.telefonnummer) &&
    visibleText(left.mobiltelefon) === visibleText(right.mobiltelefon) &&
    visibleText(left.funktion) === visibleText(right.funktion) &&
    visibleText(left.mail) === visibleText(right.mail) &&
    visibleText(left.team_mail) === visibleText(right.team_mail) &&
    documents(left) === documents(right)
  );
}

/**
 * Eine Listen-Antwort, die noch von vor dem Speichern stammt, darf die gerade
 * geschriebenen Felder nicht wieder leeren. Sobald die Liste dieselben Felder
 * enthält, gilt der Serverstand.
 */
export function preserveSavedAnsprechpartner(
  rows: KontaktAnsprechpartner[],
  saved: KontaktAnsprechpartner,
): KontaktAnsprechpartner[] {
  const current = rows.find((row) => row.id === saved.id);
  if (current && ansprechpartnerFieldsMatch(current, saved)) return rows;
  return mergeSavedAnsprechpartner(rows, saved);
}

export function apFormFromKontakt(ap: KontaktAnsprechpartner): ApForm {
  return {
    anrede: ap.anrede || "",
    vorname: ap.vorname || "",
    nachname: ap.nachname || "",
    telefonnummer: ap.telefonnummer || "",
    mobiltelefon: ap.mobiltelefon || "",
    funktion: ap.funktion || "",
    mail: ap.mail || "",
    teamMail: ap.team_mail || "",
    teamMailDokumentarten: orderedTeamMailArten(ap.team_mail_dokumentarten),
  };
}

export function validateApForm(form: ApForm): string | null {
  if (!form.vorname.trim() && !form.nachname.trim()) {
    return "Bitte mindestens Vor- oder Nachname eingeben.";
  }
  return null;
}

export function apPayloadFromForm(
  form: ApForm,
  geschaeftspartnerId: number,
): KontaktAnsprechpartnerIn {
  return {
    anrede: form.anrede.trim(),
    vorname: form.vorname.trim(),
    nachname: form.nachname.trim(),
    telefonnummer: form.telefonnummer.trim(),
    mobiltelefon: form.mobiltelefon.trim(),
    funktion: form.funktion.trim(),
    mail: form.mail.trim(),
    team_mail: form.teamMail.trim(),
    team_mail_dokumentarten: orderedTeamMailArten(form.teamMailDokumentarten),
    geschaeftspartner_id: geschaeftspartnerId,
  };
}
